import {MdDoorLite} from "../../model/door/MdDoorLite";

// How close (in screen pixels, so the feel is the same at every zoom) an edge has to be
// before it locks onto a snap line.
export const SNAP_PX = 12;

// One snap line the drag locked onto, in leaf-local coords. The drag draws these as guides
// so it is clear what the lite lined up with.
export type SnapGuide = {
    axis: "x" | "y";
    at: number;
    // true when the line is the leaf's own centre rather than another lite's edge. The
    // guide draws it differently — a door centred lite is worth calling out.
    leafCentre: boolean;
};

export type SnapResult = {
    left: number;
    top: number;
    guides: SnapGuide[];
};

// one candidate line to snap to, and whether it is the leaf centre.
type Line = {at: number; leafCentre: boolean};

// the lines a dragged lite can lock onto along one axis: the leaf's centre, plus every
// other lite's near edge, centre and far edge.
function targetLines(others: {near: number; size: number}[], leafSize: number): Line[] {
    const out: Line[] = [{at: leafSize / 2, leafCentre: true}];

    for(const o of others) {
        out.push({at: o.near, leafCentre: false});
        out.push({at: o.near + o.size / 2, leafCentre: false});
        out.push({at: o.near + o.size, leafCentre: false});
    }

    return out;
}

// the best snap for one axis: the smallest move that brings any of the dragged lite's three
// lines (near edge, centre, far edge) onto a target line, or null when nothing is in range.
function snapAxis(axis: "x" | "y", near: number, size: number, lines: Line[], tolerance: number): {delta: number; guide: SnapGuide} | null {
    let best: {delta: number; line: Line} | null = null;

    // the dragged lite's own lines, as offsets from its near edge
    for(const offset of [0, size / 2, size]) {
        for(const line of lines) {
            const delta = line.at - (near + offset);
            if(Math.abs(delta) > tolerance) continue;
            if(best === null || Math.abs(delta) < Math.abs(best.delta)) best = {delta, line};
        }
    }

    if(!best) return null;
    return {delta: best.delta, guide: {axis, at: best.line.at, leafCentre: best.line.leafCentre}};
}

// Snap a dragged lite's top-left against the leaf centre and its sibling lites. Each axis
// snaps on its own, so a lite can lock to the door centreline horizontally while it lines up
// with another lite's top edge vertically.
//
// `others` is the lites on the same leaf, minus the one being dragged: a lite never snaps to
// itself, and the two leaves of a pair are laid out independently.
export function snapLiteMove(props: {
    left: number;
    top: number;
    width: number;
    height: number;
    leafWidth: number;
    leafHeight: number;
    others: MdDoorLite[];
    // SNAP_PX converted into leaf-local units for the current zoom.
    tolerance: number;
}): SnapResult {
    const {left, top, width, height, leafWidth, leafHeight, others, tolerance} = props;

    const xLines = targetLines(
        others.map(o => ({near: o.offsetLeft.get(), size: o.width.get()})),
        leafWidth,
    );
    const yLines = targetLines(
        others.map(o => ({near: o.offsetTop.get(), size: o.height.get()})),
        leafHeight,
    );

    const x = snapAxis("x", left, width, xLines, tolerance);
    const y = snapAxis("y", top, height, yLines, tolerance);

    const guides: SnapGuide[] = [];
    if(x) guides.push(x.guide);
    if(y) guides.push(y.guide);

    return {
        left: left + (x?.delta ?? 0),
        top: top + (y?.delta ?? 0),
        guides: guides,
    };
}
